import { captureException, captureMessage } from "@sentry/react";
import { UtilsTypes } from "@stellar/utils";

/**
 * Sentry tag marking events from internal FARO automation/test accounts.
 * Set as a string ("true"/"false") because `beforeSend` receives the raw tag value,
 * so the drop check compares against the string.
 */
export const AUTOMATION_TEST_USER_TAG = "isAutomationTestUser";

/**
 * Whether a Sentry event originates from an internal automation/test account and
 * should therefore be dropped in `beforeSend` to avoid polluting the logs.
 */
export function isAutomationTestEvent(event) {
  return event.tags?.[AUTOMATION_TEST_USER_TAG] === "true";
}

// Extra tags we send to Sentry for easy filtering in the Sentry UI
// This way we can find the responsible team more easily

/**
 * Get the application section (context) where the error was triggered.
 */
function getSectionTag() {
  const pathName = window.location.pathname;
  if (`${pathName}/`.includes("/users/members/")) {
    return "members-list";
  }
  if (`${pathName}/`.includes("/users/teams/")) {
    return "teams-list";
  }
  if (`${pathName}/`.includes("/analytics/workspace/")) {
    return "analytics";
  }
  if (`${pathName}/`.includes(`/projects/${UtilsTypes.ProjectArchivingState.active}/`) || `${pathName}/`.includes(`/projects/${UtilsTypes.ProjectArchivingState.archived}/`)) {
    return "projects-list";
  }
  if (`${pathName}/`.includes("/settings/general/")) {
    return "workspace-settings";
  }
  if (/^\/[^/]+\/groups\/?$/.test(pathName)) {
    return "groups-list";
  }
  if (/^\/projects\/[^/]+\/details\/?$/.test(pathName)) {
    return "project-details";
  }
  if (/^\/[^/]+\/groups\/[^/]+\/members\/?$/.test(pathName)) {
    return "groups-members";
  }
  if (/^\/[^/]+\/groups\/[^/]+\/projects\/?$/.test(pathName)) {
    return "groups-projects";
  }
  if (/^\/[^/]+\/users\/members\/[^/]+\/account\/?$/.test(pathName)) {
    return "account-settings";
  }
  if (/^\/[^/]+\/users\/members\/[^/]+\/profile\/?$/.test(pathName)) {
    return "user-profile";
  }
  return "other";
}

/** Avoid "Object captured as exception with keys: message, name, stack" without any info. */
export class ErrorFromJson extends Error {
  constructor(obj) {
    super(obj.message);
    try {
      if (typeof obj.name === "string") {
        this.name = obj.name;
      }
      if (typeof obj.stack === "string") {
        this.stack = obj.stack;
      }
    } catch {
      // Catch errors, just in case that some browsers disallow setting e.g. the stack.
    }
  }
}

/**
 * Capturing Errors and Events in Sentry.
 * A simple text should be passed as message, additional data should be passed as data
 * https://github.com/getsentry/sentry-javascript/issues/1607#issuecomment-825887112
 * https://github.com/getsentry/sentry-react-native/issues/1033#issuecomment-2143619805
 */
export function sentryCaptureError({
  error,
  title,
  data,
  isAssertionError = false
}) {
  const tags = {
    section: getSectionTag()
  };
  let errorObject = error;
  if (typeof error === "string") {
    // Avoid <unknown> as the issue name.
    errorObject = new Error(error);
  } else if (!(error instanceof Error) && typeof error?.message === "string") {
    // Avoid "Object captured as exception with keys: message, name, stack" without any info.
    errorObject = new ErrorFromJson(error);
  }

  // Mark the assertion error with "AssertionError" title for easier filtering in Sentry
  if (isAssertionError) {
    errorObject.name = "AssertionError";
  }
  captureException(errorObject, scope => {
    if (title) {
      // Set transaction name instead of the default current uri, which is useless
      // this way it's easier see the specific problem in the list and filter specific problems/errors
      scope.setTransactionName(title);
      scope.addBreadcrumb({
        type: "error",
        category: "error",
        level: "error",
        message: title
      });
    }
    if (data) {
      scope.setContext("extra-data", data);
    }
    scope.setTags(tags);
    return scope;
  });
}

/**
 * Capturing warnings as messages in Sentry.
 */
export function sentryCaptureWarning({
  title,
  data,
  warningType
}) {
  const tags = {
    section: getSectionTag(),
    ...{
      warningType
    }
  };
  captureMessage(title, scope => {
    scope.setLevel("warning");
    if (data) {
      scope.setContext("extra-data", data);
    }
    scope.setTags(tags);
    scope.addBreadcrumb({
      type: "info",
      category: "warning",
      level: "warning",
      message: title
    });
    return scope;
  });
}